import React from 'react';
import type { AddItem } from '../LayerRows';
import { isShadowCss, parseShadowLayers, type ShadowLayer, type ShadowProperty } from './shadow-layers';
import css from './BoxShadowSettings.scss';

type Preset = { label: string; value: string; group?: string };

// Written in px; each host converts them to the lengths it stores
const PRESETS: Record<ShadowProperty, Preset[]> = {
	boxShadow: [
		{ label: 'Drop shadow', value: '0 4px 12px 0 rgba(0, 0, 0, 0.2)' },
		{ label: 'Inner shadow', value: 'inset 0 2px 6px 0 rgba(0, 0, 0, 0.25)' },
		{ label: 'Soft', group: 'Presets', value: '0 1px 2px 0 rgba(0, 0, 0, 0.08), 0 2px 8px 0 rgba(0, 0, 0, 0.08)' },
		{
			label: 'Raised',
			group: 'Presets',
			value: '0 2px 4px 0 rgba(0, 0, 0, 0.12), 0 8px 16px 0 rgba(0, 0, 0, 0.12)',
		},
		{
			label: 'Floating',
			group: 'Presets',
			value: '0 2px 4px 0 rgba(0, 0, 0, 0.08), 0 8px 16px 0 rgba(0, 0, 0, 0.1), 0 24px 48px 0 rgba(0, 0, 0, 0.12)',
		},
		{
			label: 'Glass edge',
			group: 'Presets',
			value: '0 8px 24px 0 rgba(12, 71, 104, 0.12), inset 0 1px 0 0 rgba(255, 255, 255, 0.4)',
		},
		{ label: 'Ring', group: 'Presets', value: '0 0 0 1px rgba(0, 0, 0, 0.12)' },
	],
	textShadow: [
		{ label: 'Drop shadow', value: '2px 2px 3px rgba(0, 0, 0, 0.3)' },
		{ label: 'Glow', value: '0 0 8px rgba(255, 255, 255, 0.8)' },
		{ label: 'Soft', group: 'Presets', value: '0 1px 2px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(0, 0, 0, 0.15)' },
		{ label: 'Neon', group: 'Presets', value: '0 0 4px #7df9ff, 0 0 14px rgba(0, 200, 255, 0.7)' },
	],
};

export const SHADOW_NAMES: Record<ShadowProperty, string> = { boxShadow: 'Box shadow', textShadow: 'Text shadow' };

/**
 * The Add shadow menu. `add` gets the item's layers in px, and whether they replace the others: a plain shadow goes
 * on top of them, while a preset is a whole look.
 */
export function getShadowAddItems(
	property: ShadowProperty,
	add: (layers: ShadowLayer[], replaces: boolean) => void
): AddItem[] {
	return PRESETS[property].map(preset => {
		const layers = parseShadowLayers(preset.value, property).filter(
			(layer): layer is ShadowLayer => !isShadowCss(layer)
		);
		return {
			label: preset.label,
			group: preset.group,
			meta: preset.group ? `${layers.length} layer${layers.length > 1 ? 's' : ''}` : undefined,
			thumb: (
				<span className={css.tile} aria-hidden="true">
					{property === 'boxShadow' ? (
						<span className={css.tileBox} style={{ boxShadow: preset.value }} />
					) : (
						<span className={css.tileText} style={{ textShadow: preset.value }}>
							A
						</span>
					)}
				</span>
			),
			add: () => add(layers, !!preset.group),
		};
	});
}
